'use client';
import { useCallback, useEffect, useState } from 'react';

export type DockSide = 'left' | 'right' | 'top' | 'bottom';
export interface DockState { open: boolean; side: DockSide; width: number; height: number }

const KEY = 'advisor:dock:command';
const DEFAULT: DockState = { open: false, side: 'right', width: 336, height: 420 };
const MIN_WIDTH = 320;
const SIDES: readonly DockSide[] = ['left', 'right', 'top', 'bottom'];
const maxWidth = () => (typeof window !== 'undefined' ? window.innerWidth : 1920) * 0.96;
const maxHeight = () => (typeof window !== 'undefined' ? window.innerHeight : 1080) * 0.85;
const clampWidth = (w: number) => Math.max(MIN_WIDTH, Math.min(w, maxWidth()));
const clampHeight = (h: number) => Math.max(240, Math.min(h, maxHeight()));
function read(): DockState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT;
    const value = JSON.parse(raw) as Partial<DockState>;
    return {
      open: !!value.open,
      side: SIDES.includes(value.side as DockSide) ? value.side as DockSide : DEFAULT.side,
      width: clampWidth(Number(value.width ?? DEFAULT.width)),
      height: clampHeight(Number(value.height ?? DEFAULT.height)),
    };
  } catch { return DEFAULT; }
}
function write(state: DockState) { try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* best effort */ } }

export function useDockState() {
  const [state, setState] = useState(DEFAULT);
  useEffect(() => { setState(read()); }, []);
  const update = useCallback((fn: (s: DockState) => DockState, persist = true) => setState((current) => {
    const next = fn(current);
    if (persist) write(next);
    return next;
  }), []);
  return {
    state,
    setOpen: useCallback((open: boolean) => update((current) => ({ ...current, open })), [update]),
    setSide: useCallback((side: DockSide) => update((current) => ({ ...current, side })), [update]),
    sizeRange: { width: { min: MIN_WIDTH, max: Math.max(MIN_WIDTH, maxWidth()) }, height: { min: 240, max: Math.max(240, maxHeight()) } },
    setWidth: useCallback((width: number, persist = true) => update((current) => ({ ...current, width: clampWidth(width) }), persist), [update]),
    setHeight: useCallback((height: number, persist = true) => update((current) => ({ ...current, height: clampHeight(height) }), persist), [update]),
    finishResize: useCallback(() => update((current) => current), [update]),
    resetSize: useCallback(() => update((current) => ({
      ...current,
      ...(current.side === 'left' || current.side === 'right'
        ? { width: clampWidth(DEFAULT.width) } : { height: clampHeight(DEFAULT.height) }),
    })), [update]),
  };
}
export type UseDockState = ReturnType<typeof useDockState>;
